import { useEffect, useRef, useState } from "react";
import { CheckCircle2, XCircle, ArrowRight, Loader2, Timer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, formatError } from "@/lib/api";

const LETTERS = ["A", "B", "C", "D"];

export const OptionButton = ({ i, text, state, disabled, onClick }) => {
  const styles = {
    idle: "border-line bg-surface hover:border-aqua hover:bg-aqua-soft/40",
    correct: "border-emerald-500 bg-emerald-50 text-emerald-900",
    wrong: "border-red-400 bg-red-50 text-red-900",
    muted: "border-line bg-surface opacity-60",
  };
  return (
    <button type="button" data-testid={`mcq-option-${i}`} disabled={disabled} onClick={onClick}
      className={`flex w-full items-start gap-4 rounded-xl border-2 p-4 text-left transition-colors ${styles[state]}`}>
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-elevated font-mono text-xs font-semibold text-ink-2">{LETTERS[i]}</span>
      <span className="flex-1 text-sm leading-relaxed sm:text-base">{text}</span>
      {state === "correct" && <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />}
      {state === "wrong" && <XCircle className="h-5 w-5 shrink-0 text-red-500" />}
    </button>
  );
};

export function optionState(i, fb) {
  if (!fb) return "idle";
  if (i === fb.correct_index) return "correct";
  if (i === fb.selected) return "wrong";
  return "muted";
}

function useCountdown(deadline) {
  const calc = () => (deadline ? Math.max(0, Math.floor((new Date(deadline) - Date.now()) / 1000)) : null);
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    if (!deadline) return undefined;
    const t = setInterval(() => setLeft(calc()), 1000);
    return () => clearInterval(t);
  }, [deadline]); // eslint-disable-line react-hooks/exhaustive-deps
  return left;
}

export default function QuizPlayer({ session, onFinish }) {
  const [feedback, setFeedback] = useState(session.answered || {});
  const firstUnanswered = session.questions.findIndex((q) => !(String(q.index) in (session.answered || {})));
  const [idx, setIdx] = useState(firstUnanswered === -1 ? session.questions.length - 1 : firstUnanswered);
  const [busy, setBusy] = useState(false);
  const q = session.questions[idx];
  const fb = feedback[String(idx)];
  const total = session.questions.length;
  const isLast = idx === total - 1;
  const correctSoFar = Object.values(feedback).filter((f) => f.selected === f.correct_index).length;
  const left = useCountdown(session.deadline);
  const autoDone = useRef(false);
  useEffect(() => {
    if (left === 0 && !autoDone.current) { autoDone.current = true; toast.info("Time's up! Submitting your quiz."); finish(); }
  }, [left]); // eslint-disable-line react-hooks/exhaustive-deps

  const answer = async (sel) => {
    if (fb || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/quiz/session/${session.id}/answer`, { index: idx, selected: sel });
      setFeedback((f) => ({ ...f, [String(idx)]: data }));
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };

  const finish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quiz/session/${session.id}/finish`);
      onFinish(data);
    } catch (e) { toast.error(formatError(e)); setBusy(false); }
  };

  return (
    <div data-testid="quiz-player" className="mx-auto max-w-3xl fade-up">
      <div className="mb-6 flex items-center justify-between text-sm">
        <span data-testid="quiz-progress-label" className="font-mono text-ink-3">Question {idx + 1} / {total}</span>
        {left != null && <span data-testid="quiz-timer" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-xs font-semibold ${left < 60 ? "bg-amber-100 text-amber-800" : "bg-elevated text-ink"}`}><Timer className="h-3.5 w-3.5" />{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</span>}
        <span data-testid="quiz-running-score" className="font-medium text-aqua">{correctSoFar} correct</span>
      </div>
      <Progress value={((Object.keys(feedback).length) / total) * 100} className="mb-8 h-1.5" />
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3">{q.topic}</p>
      <h2 data-testid="quiz-question-text" className="mb-8 text-xl font-bold leading-snug text-ink sm:text-2xl">{q.question}</h2>
      <div className="flex flex-col gap-3">
        {q.options.map((o, i) => (
          <OptionButton key={i} i={i} text={o} state={optionState(i, fb)} disabled={!!fb || busy || left === 0} onClick={() => answer(i)} />
        ))}
      </div>
      {fb && (
        <div data-testid="quiz-feedback" className={`mt-6 rounded-xl border-l-4 p-5 fade-up ${fb.is_correct ? "border-emerald-500 bg-emerald-50/70" : "border-amber-500 bg-amber-50/70"}`}>
          <p className="mb-1 text-sm font-bold text-ink">{fb.is_correct ? "Correct!" : `Not quite — the answer is ${LETTERS[fb.correct_index]}.`}</p>
          <p data-testid="quiz-explanation" className="text-sm leading-relaxed text-ink-2">{fb.explanation}</p>
        </div>
      )}
      <div className="mt-8 flex justify-end">
        {isLast ? (
          <Button data-testid="quiz-finish-btn" disabled={!fb || busy} onClick={finish} className="rounded-full px-6">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "See my results"}
          </Button>
        ) : (
          <Button data-testid="quiz-next-btn" disabled={!fb} onClick={() => setIdx(idx + 1)} className="rounded-full px-6">
            Next question <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
